import { useEffect, useState } from 'react';

import { useTypingAnimation } from '@highlight-cp/hooks';

interface UsePlaceholderProps {
  placeholders?: string[];
  placeholder?: string; // Fallback placeholder string
  initialDelay?: number; // ms to wait before showing the animated placeholder (default: 4000)
}

/**
 * ### usePlaceholder
 *
 * @description
 * Hook that manages placeholder display with animated typing effect.
 * Returns the animated placeholder after an initial delay, or the fallback placeholder otherwise.
 * Internally uses useTypingAnimation to handle the typing animation.
 *
 * @example
 * const placeholder = usePlaceholder({
 *   placeholders: ['Type something...', 'Ask a question...'],
 *   placeholder: 'Enter text...',
 *   initialDelay: 4000
 * });
 *
 * @param props - Hook configuration
 * @param props.placeholders - Array of placeholder strings for animation
 * @param props.placeholder - Fallback placeholder string to show before animation starts
 * @param props.initialDelay - Milliseconds to wait before showing animated placeholder (default: 4000)
 * @returns The placeholder string to display (animated or fallback)
 */
const usePlaceholder = ({
  placeholders,
  placeholder,
  initialDelay = 4000,
}: UsePlaceholderProps): string => {
  const [shouldShowAnimated, setShouldShowAnimated] = useState(false);

  const animatedPlaceholder = useTypingAnimation({
    strings: placeholders && placeholders.length > 0 ? placeholders : undefined,
    enabled: shouldShowAnimated,
  });

  useEffect(() => {
    if (placeholders && placeholders.length > 0) {
      const timeoutId = setTimeout(() => {
        setShouldShowAnimated(true);
      }, initialDelay);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setShouldShowAnimated(false);
    }
  }, [placeholders, initialDelay]);

  const shouldUseAnimated = shouldShowAnimated && placeholders && placeholders.length > 0;

  return shouldUseAnimated ? animatedPlaceholder : (placeholder ?? '');
};

export default usePlaceholder;
